import { PALETTE } from '@overlayqa/shared';
/**
 * The logo box: the preview of the stored logo, the drop target, and the
 * click-to-choose affordance, in one element (audit 2026-09-03, Jakob's Law
 * and Proximity rows).
 *
 * PAINTED WITH THE SAVED HEADER BACKGROUND. The box used to be white while the
 * header the logo lands on uses the shared dark surface, so a dark logo looked fine here
 * and vanished on the client page. The copy even warned about it; the preview
 * could not show it.
 */
import { useRef, useState, type DragEvent, type KeyboardEvent, type ReactNode } from 'react';

/** The shipped review header chrome, `.rp-header` in review-page/shell.ts. */
export const DEFAULT_HEADER_BACKGROUND = PALETTE.surface.dark;

interface BrandingLogoDropzoneProps {
  logoUrl: string | null;
  backgroundColor: string | null;
  disabled: boolean;
  uploading: boolean;
  onFiles: (files: FileList | File[]) => void;
  onChoose: () => void;
}

function hasFiles(ev: DragEvent<HTMLDivElement>): boolean {
  return Array.from(ev.dataTransfer?.types ?? []).includes('Files');
}

export function BrandingLogoDropzone({
  logoUrl, backgroundColor, disabled, uploading, onFiles, onChoose,
}: BrandingLogoDropzoneProps) {
  const [dragging, setDragging] = useState(false);
  // dragenter/dragleave fire for every child element crossed; a depth counter
  // is the only thing that keeps the highlight steady across them.
  const depth = useRef(0);

  const onDragEnter = (ev: DragEvent<HTMLDivElement>) => {
    if (disabled || !hasFiles(ev)) return;
    ev.preventDefault();
    depth.current += 1;
    setDragging(true);
  };
  const onDragOver = (ev: DragEvent<HTMLDivElement>) => {
    if (disabled || !hasFiles(ev)) return;
    ev.preventDefault();
    if (!dragging) setDragging(true);
  };
  const onDragLeave = (ev: DragEvent<HTMLDivElement>) => {
    if (disabled) return;
    ev.preventDefault();
    depth.current = Math.max(0, depth.current - 1);
    if (depth.current === 0) setDragging(false);
  };
  const onDrop = (ev: DragEvent<HTMLDivElement>) => {
    if (disabled) return;
    ev.preventDefault();
    depth.current = 0;
    setDragging(false);
    const files = ev.dataTransfer?.files;
    if (files && files.length > 0) onFiles(files);
  };
  const onKeyDown = (ev: KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return;
    if (ev.key === 'Enter' || ev.key === ' ') {
      ev.preventDefault();
      onChoose();
    }
  };

  let content: ReactNode;
  if (uploading) {
    content = (
      <span className="inline-flex items-center gap-2 text-xs text-white/90">
        <span className="h-3 w-3 animate-spin rounded-full border-2 border-white/40 border-t-white" aria-hidden="true" />
        Uploading...
      </span>
    );
  } else if (dragging) {
    content = <span className="text-xs font-medium text-white/90">Drop to upload</span>;
  } else if (logoUrl) {
    content = <img src={logoUrl} alt="Your team logo" className="max-h-12 max-w-32 object-contain" />;
  } else {
    content = <span className="px-3 text-center text-xs text-white/70">Drop a logo here, or click to upload</span>;
  }

  return (
    <div
      data-testid="branding-logo-dropzone"
      data-dragging={dragging ? 'true' : 'false'}
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled ? 'true' : 'false'}
      aria-label="Logo. Drop an image here, or press Enter to choose a file"
      className={[
        'flex h-16 w-40 items-center justify-center rounded-md border transition-colors',
        dragging ? 'border-primary-500 ring-2 ring-primary-500/30' : 'border-gray-200',
        disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
      ].join(' ')}
      style={{ backgroundColor: backgroundColor ?? DEFAULT_HEADER_BACKGROUND }}
      onClick={() => { if (!disabled) onChoose(); }}
      onKeyDown={onKeyDown}
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      {content}
    </div>
  );
}
